import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Lock, Star } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Loader } from "@/components/cast/Common";
import { SeriesBadge } from "@/components/cast/SeriesBadge";
import { VerseCollection } from "@/components/cast/Highlights";
import { SeasonStickers } from "@/components/cast/KidsCatch";
import { VerseGifts } from "@/components/cast/VerseGifts";

const TILT = [-6, 4, -3, 7, -5, 3, 5, -4];

export function StickerBook() {
  const { kid } = useAuth();
  const [eps, setEps] = useState<any[] | null>(null);
  const [badges, setBadges] = useState<any[]>([]);
  useEffect(() => {
    api.get(`/kids/${kid.id}/series`).then((r) => {
      setBadges(r.data.map((s: any) => s.badge));
      setEps(r.data.flatMap((s: any) => s.episodes.map((e: any) => ({ ...e, cover: s.cover, series: s.title }))));
    });
  }, [kid.id]);
  if (!eps) return <Loader />;
  const earned = eps.filter((e) => e.completed);
  const stars = earned.reduce((n, e) => n + e.stars, 0);

  return (
    <div>
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div><p className="text-lg font-semibold text-[#E07A5F]">{kid.name}'s</p><h1 className="mt-1 text-4xl font-semibold text-slate-800 sm:text-5xl">Sticker Book</h1></div>
        <div className="flex gap-3">
          <div className="rounded-full bg-[#81B29A]/20 px-5 py-2.5 text-lg font-semibold text-[#3f6b55]" data-testid="sticker-count">{earned.length} / {eps.length} stickers</div>
          <div className="flex items-center gap-2 rounded-full bg-amber-100 px-5 py-2.5 text-lg font-semibold text-amber-800" data-testid="sticker-stars"><Star className="h-5 w-5 fill-amber-400 text-amber-400" />{stars} stars</div>
        </div>
      </div>
      <section className="mt-10 rounded-[2rem] bg-gradient-to-br from-amber-100/70 to-white p-6 sm:p-8" data-testid="kids-saved-verses">
        <h2 className="text-2xl font-semibold text-slate-800">My favourite verses</h2>
        <p className="mb-6 text-slate-500">Verses you saved from your Bible adventures.</p>
        <VerseCollection kids />
      </section>
      <SeasonStickers kidId={kid.id} />
      <VerseGifts kidId={kid.id} />
      <section className="mt-10 rounded-[2rem] bg-gradient-to-br from-[#81B29A]/15 to-amber-50 p-6 sm:p-8" data-testid="series-badges">
        <h2 className="text-2xl font-semibold text-slate-800">Series badges</h2>
        <p className="text-slate-500">Finish every episode in a story series to earn its badge.</p>
        <div className="mt-6 flex flex-wrap gap-8">
          {badges.map((b) => (
            <div key={b.series_id} className={`flex w-32 flex-col items-center text-center ${b.earned ? "sticker-pop" : "opacity-80"}`}>
              <SeriesBadge badge={b} size={96} />
              <p className={`mt-2 font-semibold ${b.earned ? "text-slate-800" : "text-slate-400"}`}>{b.name}</p>
              <p className="text-xs text-slate-500">{b.series_title}</p>
            </div>
          ))}
        </div>
      </section>
      <div className="mt-10 grid grid-cols-2 gap-6 rounded-[2rem] border-4 border-dashed border-[#E2DBCE] bg-[#FFF8E8] p-6 sm:grid-cols-3 sm:p-10 lg:grid-cols-4" data-testid="sticker-grid">
        {eps.map((e, i) => e.completed ? (
          <div key={e.id} className="sticker-pop flex flex-col items-center text-center" style={{ animationDelay: `${i * 0.08}s` }} data-testid={`sticker-earned-${e.id}`}>
            <div className="relative transition-transform hover:scale-110 hover:rotate-0" style={{ transform: `rotate(${TILT[i % TILT.length]}deg)` }}>
              <img src={e.cover} alt="" className="h-32 w-32 rounded-full border-[6px] border-white object-cover shadow-xl sm:h-36 sm:w-36" />
              <span className="absolute -bottom-2 left-1/2 flex -translate-x-1/2 gap-0.5 rounded-full bg-white px-2.5 py-1 shadow-md">
                {Array.from({ length: Math.max(e.stars_total, 1) }).map((_, k) => <Star key={k} className={`h-4 w-4 ${k < e.stars ? "fill-amber-400 text-amber-400" : "text-slate-300"}`} />)}
              </span>
            </div>
            <p className="mt-5 text-lg font-semibold text-slate-800">{e.title}</p>
            <p className="text-sm text-slate-500">{e.series}</p>
          </div>
        ) : (
          <Link key={e.id} to={`/kids/episode/${e.id}`} className="flex flex-col items-center text-center opacity-80 transition-opacity hover:opacity-100" data-testid={`sticker-locked-${e.id}`}>
            <div className="relative flex h-32 w-32 items-center justify-center overflow-hidden rounded-full border-4 border-dashed border-slate-300 bg-white sm:h-36 sm:w-36">
              <img src={e.cover} alt="" className="absolute inset-0 h-full w-full object-cover opacity-25 blur-sm grayscale" />
              <Lock className="relative h-8 w-8 text-slate-400" />
            </div>
            <p className="mt-5 text-lg font-semibold text-slate-500">{e.title}</p>
            <p className="text-sm text-slate-400">Finish the story to earn it</p>
          </Link>
        ))}
      </div>
    </div>
  );
}
